import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type RefObject,
} from "react";
import type { WaveTocEntry } from "@vida/wave-core";

const DEFAULT_SUSPEND_MS = 600;

/**
 * Scroll-spy for the thread TOC: tracks the first visible `[data-block-id]`
 * feed row inside the `.main` scroll container (the viewport is NOT the
 * scroller in the Wave shell).
 */
export function useWaveTocActiveBlock(
  entries: WaveTocEntry[],
  containerRef: RefObject<HTMLElement | null>,
  options: { enabled: boolean; rebindKey?: string },
): {
  activeBlockId: string | null;
  setActiveBlockId: (blockId: string | null) => void;
  suspendSpy: (ms?: number) => void;
} {
  const { enabled, rebindKey = "" } = options;
  const [activeBlockId, setActiveBlockId] = useState<string | null>(null);
  const suspendUntilRef = useRef(0);
  const entriesRef = useRef(entries);
  entriesRef.current = entries;

  const suspendSpy = useCallback((ms: number = DEFAULT_SUSPEND_MS) => {
    suspendUntilRef.current = Date.now() + ms;
  }, []);

  const entriesKey = entries.map((e) => e.blockId).join("|");

  useEffect(() => {
    if (!enabled) return;
    const container = containerRef.current;
    if (!container || typeof IntersectionObserver === "undefined") return;
    const root = container.closest(".main");

    const visibleIds = new Set<string>();
    const observer = new IntersectionObserver(
      (observed) => {
        for (const item of observed) {
          const id = (item.target as HTMLElement).dataset.blockId;
          if (!id) continue;
          if (item.isIntersecting) visibleIds.add(id);
          else visibleIds.delete(id);
        }
        if (Date.now() < suspendUntilRef.current) return;
        if (visibleIds.size === 0) return;
        const first = entriesRef.current.find((e) => visibleIds.has(e.blockId));
        if (first) setActiveBlockId(first.blockId);
      },
      {
        root,
        // Active band: row entering the upper third of the scroller.
        rootMargin: "-20% 0px -70% 0px",
        threshold: 0,
      },
    );

    const targets = container.querySelectorAll<HTMLElement>("[data-block-id]");
    targets.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [enabled, entriesKey, rebindKey, containerRef]);

  return { activeBlockId, setActiveBlockId, suspendSpy };
}
